import React, { useState, useEffect, useReducer, useCallback } from "react";
import { toast } from "react-toastify";

import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import Avatar from "@material-ui/core/Avatar";
import Checkbox from "@material-ui/core/Checkbox";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import SearchIcon from "@material-ui/icons/Search";
import GroupIcon from "@material-ui/icons/Group";
import { makeStyles } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";
import { createApi } from "../../services/api";
import toastError from "../../errors/toastError";
import { BotaoCancelar, BotaoConfirmar } from "../BotoesModal";

// Mesmo teto do backend (EncaminharMensagemService.MAX_DESTINATARIOS): barrar aqui
// evita o atendente marcar 50 contatos e só descobrir o limite no envio.
const MAX_DESTINATARIOS = 30;

const reducer = (state, action) => {
	if (action.type === "LOAD_CONTACTS") {
		const contatos = action.payload;
		const novos = [];
		contatos.forEach((contato) => {
			const indice = state.findIndex((c) => c.id === contato.id);
			if (indice !== -1) {
				state[indice] = contato;
			} else {
				novos.push(contato);
			}
		});
		return [...state, ...novos];
	}

	if (action.type === "RESET") {
		return [];
	}

	return state;
};

const useStyles = makeStyles((theme) => ({
	busca: {
		width: "100%",
		marginBottom: theme.spacing(1),
	},

	lista: {
		height: 340,
		overflowY: "auto",
		...theme.scrollbarStyles,
	},

	aviso: {
		padding: theme.spacing(2),
		textAlign: "center",
	},

	carregando: {
		display: "flex",
		justifyContent: "center",
		padding: theme.spacing(1),
	},
}));

/**
 * Modal de escolha dos destinatários do encaminhamento (#14436).
 *
 * <p>Lista só os contatos da conexão da conversa (RN03) — quem alimenta é
 * `GET /contacts/conexao/:whatsappId`, que resolve o vínculo pelos tickets. Grupos da
 * conexão entram na lista como qualquer outro destinatário.
 */
const ForwardMessageModal = ({ open, onClose, message, whatsappId, urlBackend }) => {
	const classes = useStyles();
	const apiCustom = createApi(urlBackend);

	const [searchParam, setSearchParam] = useState("");
	const [pageNumber, setPageNumber] = useState(1);
	const [hasMore, setHasMore] = useState(false);
	const [loading, setLoading] = useState(false);
	const [contacts, dispatch] = useReducer(reducer, []);
	const [enviando, setEnviando] = useState(false);

	// Guarda o contato inteiro, não só o id: o selecionado precisa continuar contado
	// (e o botão precisa saber quantos são) mesmo depois que a busca o tirar da lista.
	const [selecionados, setSelecionados] = useState({});

	const limparEstado = useCallback(() => {
		setSearchParam("");
		setPageNumber(1);
		setSelecionados({});
		dispatch({ type: "RESET" });
	}, []);

	useEffect(() => {
		if (!open) {
			limparEstado();
		}
	}, [open, limparEstado]);

	useEffect(() => {
		dispatch({ type: "RESET" });
		setPageNumber(1);
	}, [searchParam, whatsappId]);

	useEffect(() => {
		if (!open || !whatsappId) return;

		setLoading(true);
		const debounce = setTimeout(() => {
			const buscarContatos = async () => {
				try {
					const { data } = await apiCustom.get(`/contacts/conexao/${whatsappId}`, {
						params: { searchParam, pageNumber },
					});
					dispatch({ type: "LOAD_CONTACTS", payload: data.contacts });
					setHasMore(data.hasMore);
				} catch (err) {
					toastError(err);
				}
				setLoading(false);
			};
			buscarContatos();
		}, 500);

		return () => clearTimeout(debounce);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, whatsappId, searchParam, pageNumber]);

	const handleScroll = (e) => {
		if (!hasMore || loading) return;
		const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
		if (scrollHeight - (scrollTop + 100) < clientHeight) {
			setPageNumber((anterior) => anterior + 1);
		}
	};

	const alternarSelecao = (contato) => {
		setSelecionados((anterior) => {
			if (anterior[contato.id]) {
				const copia = { ...anterior };
				delete copia[contato.id];
				return copia;
			}
			if (Object.keys(anterior).length >= MAX_DESTINATARIOS) {
				toast.warning(i18n.t("forwardMessageModal.maxContatos", { max: MAX_DESTINATARIOS }));
				return anterior;
			}
			return { ...anterior, [contato.id]: contato };
		});
	};

	const idsSelecionados = Object.keys(selecionados).map(Number);

	const handleEncaminhar = async () => {
		if (idsSelecionados.length === 0) return;

		setEnviando(true);
		try {
			const { data } = await apiCustom.post(`/messages/${message.id}/encaminhar`, {
				contactIds: idsSelecionados,
			});

			// Falha em um destinatário não interrompe os demais (RNF do ticket), então o
			// retorno é parcial por natureza: o atendente precisa saber quantos foram.
			if (data.falhas > 0 && data.enviados > 0) {
				toast.warning(i18n.t("forwardMessageModal.parcial", {
					enviados: data.enviados,
					total: data.total,
				}));
			} else if (data.falhas > 0) {
				toast.error(i18n.t("forwardMessageModal.nenhumEnviado"));
			} else {
				toast.success(i18n.t("forwardMessageModal.sucesso", { total: data.enviados }));
			}
			onClose();
		} catch (err) {
			toastError(err);
		}
		setEnviando(false);
	};

	const descricao = (contato) => {
		if (contato.isGroup) return i18n.t("forwardMessageModal.grupo");
		return contato.number;
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
			<DialogTitle>{i18n.t("forwardMessageModal.title")}</DialogTitle>
			<DialogContent dividers>
				<TextField
					className={classes.busca}
					placeholder={i18n.t("forwardMessageModal.buscar")}
					value={searchParam}
					onChange={(e) => setSearchParam(e.target.value)}
					variant="outlined"
					margin="dense"
					autoFocus
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon color="action" fontSize="small" />
							</InputAdornment>
						),
					}}
				/>

				<div className={classes.lista} onScroll={handleScroll}>
					<List dense disablePadding>
						{contacts.map((contato) => (
							<ListItem
								key={contato.id}
								button
								onClick={() => alternarSelecao(contato)}
							>
								<ListItemAvatar>
									<Avatar src={contato.profilePicUrl}>
										{contato.isGroup ? <GroupIcon /> : null}
									</Avatar>
								</ListItemAvatar>
								<ListItemText
									primary={contato.name}
									secondary={descricao(contato)}
								/>
								<ListItemSecondaryAction>
									<Checkbox
										edge="end"
										color="primary"
										checked={!!selecionados[contato.id]}
										onChange={() => alternarSelecao(contato)}
									/>
								</ListItemSecondaryAction>
							</ListItem>
						))}
					</List>

					{loading && (
						<div className={classes.carregando}>
							<CircularProgress size={22} />
						</div>
					)}

					{!loading && contacts.length === 0 && (
						<Typography className={classes.aviso} color="textSecondary" variant="body2">
							{i18n.t("forwardMessageModal.semContatos")}
						</Typography>
					)}
				</div>
			</DialogContent>
			<DialogActions>
				<BotaoCancelar onClick={onClose} disabled={enviando}>
					{i18n.t("forwardMessageModal.buttons.cancel")}
				</BotaoCancelar>
				<BotaoConfirmar
					type="button"
					onClick={handleEncaminhar}
					loading={enviando}
					disabled={idsSelecionados.length === 0}
				>
					{idsSelecionados.length > 0
						? i18n.t("forwardMessageModal.buttons.okContagem", { total: idsSelecionados.length })
						: i18n.t("forwardMessageModal.buttons.ok")}
				</BotaoConfirmar>
			</DialogActions>
		</Dialog>
	);
};

export default ForwardMessageModal;
